<ng-container *transloco="let t; read: 'operations'">
  <app-modal
    [(open)]="open"
    [title]="t('templatePull.title')"
    [subtitle]="t('templatePull.subtitle', { from: preview().fromVersion, to: preview().toVersion })"
    kind="info"
    size="large"
    [primaryLabel]="t('templatePull.confirm')"
    [primaryDisabled]="busy()"
    [cancelLabel]="t('templatePull.cancel')"
    (primaryAction)="onConfirm()"
    (closed)="onClose()"
  >
    <div class="flex flex-col gap-6">
      <!-- CONFLICTS — both-sides-edited; need a choice. -->
      @if (hasConflicts()) {
        <section class="flex flex-col gap-4">
          <div>
            <h3 class="font-display text-[16px] font-bold text-ink-1">{{ t('templatePull.conflictsHeading') }}</h3>
            <p class="mt-1 text-[13px] text-ink-2">{{ t('templatePull.conflictsNote') }}</p>
          </div>

          @for (r of conflictRows(); track r.values.field) {
            @let c = r.values;
            <div class="rounded-card bg-card p-4 shadow-raised">
              <div class="mb-3 flex items-center justify-between gap-3">
                <span class="text-[13px] font-semibold text-ink-1">{{ fieldLabel(c.field) }}</span>
                <span class="text-[12px] text-ink-3">{{ baseLabel(c) }}</span>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <!-- Keep yours (mine) -->
                <button
                  type="button"
                  (click)="choose(c.field, 'mine')"
                  class="flex flex-col gap-1.5 rounded-control border p-3 text-left transition-colors"
                  [class.border-action]="isChosen(c.field, 'mine')"
                  [class.bg-action-surface]="isChosen(c.field, 'mine')"
                  [class.border-hairline]="!isChosen(c.field, 'mine')"
                  [class.hover:bg-sunken]="!isChosen(c.field, 'mine')"
                >
                  <span class="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                    {{ t('templatePull.keepYours') }}
                    @if (isChosen(c.field, 'mine')) {
                      <app-icon name="icon-checkmark" [size]="14" class="text-action" />
                    }
                  </span>
                  <span class="font-num text-[15px] font-semibold text-ink-1">{{ formatValue(c.field, c.mine) }}</span>
                </button>

                <!-- Take template (theirs) -->
                <button
                  type="button"
                  (click)="choose(c.field, 'theirs')"
                  class="flex flex-col gap-1.5 rounded-control border p-3 text-left transition-colors"
                  [class.border-action]="isChosen(c.field, 'theirs')"
                  [class.bg-action-surface]="isChosen(c.field, 'theirs')"
                  [class.border-hairline]="!isChosen(c.field, 'theirs')"
                  [class.hover:bg-sunken]="!isChosen(c.field, 'theirs')"
                >
                  <span class="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                    {{ t('templatePull.takeTemplate') }}
                    @if (isChosen(c.field, 'theirs')) {
                      <app-icon name="icon-checkmark" [size]="14" class="text-action" />
                    }
                  </span>
                  <span class="font-num text-[15px] font-semibold text-ink-1">{{ formatValue(c.field, c.theirs) }}</span>
                </button>
              </div>
            </div>
          }
        </section>
      }

      <!-- INCOMING CHANGES — template-only; applied as-is, shown read-only. -->
      @if (autoRows().length > 0) {
        <section class="flex flex-col gap-3">
          <div>
            <h3 class="font-display text-[16px] font-bold text-ink-1">{{ t('templatePull.changesHeading') }}</h3>
            <p class="mt-1 text-[13px] text-ink-2">{{ t('templatePull.changesNote') }}</p>
          </div>

          <div class="flex flex-col gap-px overflow-hidden rounded-card bg-hairline shadow-raised">
            @for (r of autoRows(); track r.values.field) {
              @let c = r.values;
              <div class="flex items-center justify-between gap-3 bg-card px-4 py-3">
                <span class="text-[13px] font-semibold text-ink-1">{{ fieldLabel(c.field) }}</span>
                <span class="flex items-center gap-2 font-num text-[14px] font-semibold">
                  <span class="text-ink-3 line-through">{{ formatValue(c.field, c.mine) }}</span>
                  <app-icon name="icon-arrow-right" [size]="14" class="text-ink-3" />
                  <span class="text-ink-1">{{ formatValue(c.field, c.theirs) }}</span>
                </span>
              </div>
            }
          </div>
        </section>
      }

      <!-- OPERATIONS — replaced wholesale; informational before/after. -->
      @if (opsChanged()) {
        <section class="flex flex-col gap-3">
          <div>
            <h3 class="font-display text-[16px] font-bold text-ink-1">{{ t('templatePull.opsHeading') }}</h3>
            <p class="mt-1 text-[13px] text-ink-2">{{ t('templatePull.opsNote') }}</p>
          </div>

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <!-- Current operations (to be replaced) -->
            <div class="flex flex-col gap-2 rounded-card bg-card p-4 shadow-raised">
              <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                {{ t('templatePull.opsCurrent', { count: currentOps().length }) }}
              </span>
              @if (currentOps().length === 0) {
                <span class="text-[13px] text-ink-3">{{ t('templatePull.opsNone') }}</span>
              } @else {
                @for (op of currentOps(); track op.id) {
                  <div class="flex flex-col">
                    <span class="truncate font-num text-[13px] font-semibold text-ink-2 line-through">{{ op.operationId }}</span>
                    <span class="truncate text-[12px] text-ink-3">{{ operationSummary(op) }}</span>
                  </div>
                }
              }
            </div>

            <!-- Incoming operations (from the template) -->
            <div class="flex flex-col gap-2 rounded-card bg-card p-4 shadow-raised">
              <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                {{ t('templatePull.opsIncoming', { count: incomingOps().length }) }}
              </span>
              @if (incomingOps().length === 0) {
                <span class="text-[13px] text-ink-3">{{ t('templatePull.opsNone') }}</span>
              } @else {
                @for (op of incomingOps(); track op.id) {
                  <div class="flex flex-col">
                    <span class="truncate font-num text-[13px] font-semibold text-ink-1">{{ op.operationId }}</span>
                    <span class="truncate text-[12px] text-ink-2">{{ operationSummary(op) }}</span>
                  </div>
                }
              }
            </div>
          </div>
        </section>
      }

      <!-- NO CHANGES — settings identical; pull only refreshes operations. -->
      @if (!hasAnyChange()) {
        <section>
          <h3 class="font-display text-[16px] font-bold text-ink-1">{{ t('templatePull.noChangesHeading') }}</h3>
          <p class="mt-1 text-[13px] text-ink-2">{{ t('templatePull.noChangesNote') }}</p>
        </section>
      }
    </div>
  </app-modal>
</ng-container>
